import { shopifyProvinces } from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import { t, useTranslation } from "@redotech/redo-i18n";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
} from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import {
  groupInput,
  input,
  InputProvider,
  nonEmptyValidator,
  Validator,
} from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { AutoCompleteInput } from "./autocomplete-input";
import { Flex } from "./flex";
import { Text } from "./text";
import { isValidPhoneNumber } from "./utils/validators";

// Define the address type for better type safety
export type AddressAutocompleteFields = {
  address1: string;
  address2?: string;
  city: string;
  province: string;
  zip: string;
  countryCode: string;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
};

// Define action handler interfaces for dependency injection
export interface AddressAutocompleteActions {
  getAddressSuggestionsFromAddressLine: (
    query: string,
    sessionToken?: string,
    countryCode?: string,
  ) => Promise<Array<{ keyToFullObject: string; displayText: string }>>;

  getAddressFromAddressLineSuggestion: (
    suggestionId: string,
  ) => Promise<AddressAutocompleteFields | null>;

  getAddressSuggestionsFromName: (
    nameSearchString: string,
  ) => Promise<
    Array<{ keyToFullObject: FulfillmentOrderAddress; displayText: string }>
  >;

  getAddressFromNameSuggestion: (
    destinationAddress: FulfillmentOrderAddress,
  ) => Promise<AddressAutocompleteFields & { name: string }>;
}

const requiredPhoneValidator = (): Validator<string | null> => (value) => {
  if (!value) return [t("Phone number is required")];
  return isValidPhoneNumber(value) ? [] : [t("Invalid phone number")];
};

const phoneValidator = (): Validator<string | null> => (value) => {
  if (!value) return [];

  return isValidPhoneNumber(value) ? [] : [t("Invalid phone number")];
};

const emailValidator = (): Validator<string | null> => (value) => {
  if (!value) return [t("Email is required")];
  return [];
};

export const createAddressAutocompleteForm = (
  requireEmail: boolean = false,
  requirePhone: boolean = false,
) =>
  groupInput({
    name: input<string>({ validator: nonEmptyValidator }),
    address1: input<string>({ validator: nonEmptyValidator }),
    address2: input<string | null>({}),
    city: input<string>({ validator: nonEmptyValidator }),
    company: input<string | null>({}),
    province: input<string | null>({}),
    zip: input<string>({ validator: nonEmptyValidator }),
    countryCode: input<RedoShopifyCountryCode>({
      validator: nonEmptyValidator,
    }),
    phone: input<string | null>({
      validator: requirePhone ? requiredPhoneValidator() : phoneValidator(),
    }),
    email: input<string | null>({
      validator: requireEmail ? emailValidator() : undefined,
    }),
  });

export const omsAddressAutocompleteForm = createAddressAutocompleteForm(
  false,
  false,
);

export const addressAutocompleteForm = createAddressAutocompleteForm(false);

export type AddressAutocompleteFormType = InputProvider.Form<
  typeof addressAutocompleteForm
>;
export type AddressAutocompleteFormValue = InputProvider.Value<
  typeof addressAutocompleteForm
>;

export interface AddressAutocompleteFormProps {
  form: AddressAutocompleteFormType;
  title?: string;
  hideEmailField?: boolean;
  hideCompanyField?: boolean;
  includeNameField?: boolean;
  autoCompleteOnName?: boolean;
  actions?: AddressAutocompleteActions;
  requireEmail?: boolean;
  requirePhone?: boolean;
}

export const useAddressAutocomplete = (
  form: AddressAutocompleteFormType,
  actions?: AddressAutocompleteActions,
) => {
  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string) => {
      if (!actions?.getAddressSuggestionsFromAddressLine) {
        return [];
      }
      return await actions.getAddressSuggestionsFromAddressLine(
        query,
        sessionToken,
        form.value.countryCode,
      );
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      if (!actions?.getAddressFromAddressLineSuggestion) {
        return null;
      }
      return await actions.getAddressFromAddressLineSuggestion(suggestionId);
    },
  );

  const getAddressSuggestionsFromName = useHandler(
    async (nameSearchString: string) => {
      if (!actions?.getAddressSuggestionsFromName) {
        return [];
      }
      return await actions.getAddressSuggestionsFromName(nameSearchString);
    },
  );

  const getAddressFromNameSuggestion = useHandler(
    async (destinationAddress: FulfillmentOrderAddress) => {
      if (!actions?.getAddressFromNameSuggestion) {
        return {
          name: destinationAddress.name ?? "",
          address1: destinationAddress.address1 ?? "",
          address2: destinationAddress.address2 ?? "",
          city: destinationAddress.city ?? "",
          province: destinationAddress.province ?? "",
          zip: destinationAddress.zip ?? "",
          countryCode: destinationAddress.countryCode as RedoShopifyCountryCode,
          email: destinationAddress.email,
          company: destinationAddress.company,
          phone: destinationAddress.phone,
        };
      }
      return await actions.getAddressFromNameSuggestion(destinationAddress);
    },
  );

  const setAddressFields = useHandler((address: AddressAutocompleteFields) => {
    form.setValue({
      ...form.value,
      address1: address.address1,
      address2: address.address2 || null,
      city: address.city,
      province: address.province,
      zip: address.zip,
      countryCode: address.countryCode as RedoShopifyCountryCode,
    });
  });

  const setAddressAndNameFields = useHandler(
    (fields: AddressAutocompleteFields & { name: string }) => {
      form.setValue({
        ...form.value,
        name: fields.name,
        address1: fields.address1,
        address2: fields.address2 || null,
        city: fields.city,
        province: fields.province,
        zip: fields.zip,
        countryCode: fields.countryCode as RedoShopifyCountryCode,
        email: fields.email ?? form.value.email,
        company: fields.company ?? form.value.company,
        phone: fields.phone ?? form.value.phone,
      });
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
    setAddressFields,
    setAddressAndNameFields,
  };
};

export const AddressAutocompleteForm = memo(function AddressAutocompleteForm({
  form,
  title: tileProp,
  hideEmailField = false,
  hideCompanyField = false,
  includeNameField = true,
  autoCompleteOnName = true,
  actions,
  requireEmail = false,
  requirePhone = false,
}: AddressAutocompleteFormProps) {
  const { t } = useTranslation();
  const addressUtils = useAddressAutocomplete(form, actions);
  const [useManualProvinceEntry, setUseManualProvinceEntry] = useState(false);
  const title = tileProp ?? t("Recipient information");

  const {
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    setAddressFields,
    setAddressAndNameFields,
  } = addressUtils;

  const selectedCountryCode = useMemo(() => {
    return form.value.countryCode ?? RedoShopifyCountryCode.Us;
  }, [form.value.countryCode]);

  const availableProvinces = useMemo(() => {
    return shopifyProvinces.filter(
      (p) => p.countryCode === selectedCountryCode,
    );
  }, [selectedCountryCode]);

  return (
    <Flex dir="column" gap="3xl">
      {title && (
        <Text fontSize="md" fontWeight="semibold">
          {title}
        </Text>
      )}

      {includeNameField &&
        (autoCompleteOnName && actions ? (
          <AutoCompleteInput<
            AddressAutocompleteFields & { name: string },
            FulfillmentOrderAddress
          >
            dataTestId="full-name-input"
            getSuggestions={getAddressSuggestionsFromName}
            label={t("Name")}
            pickSuggestion={getAddressFromNameSuggestion}
            placeholder={t("Enter name")}
            setAutocompleteFields={setAddressAndNameFields}
            setValue={(value) => {
              form.inputs.name.setValue(value);
            }}
            value={form.value.name}
          />
        ) : (
          <RedoTextInput
            dataTestId="full-name-input"
            label={t("Name")}
            placeholder={t("Enter name")}
            required
            setValue={form.inputs.name.setValue}
            value={form.value.name}
          />
        ))}

      {!hideCompanyField && (
        <RedoTextInput
          dataTestId="address-company-input"
          label={t("Company")}
          placeholder={t("Enter company")}
          setValue={form.inputs.company.setValue}
          value={form.value.company ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
        dataTestId="country-input"
        label={t("Country")}
        options={Object.values(RedoShopifyCountryCode).map((code) => ({
          id: code,
          type: "text",
          text: getCountryNameFromCode(code),
          value: code,
        }))}
        optionSelected={({ item: country }) => {
          form.inputs.countryCode.setValue(country.value);
          form.inputs.province.setValue(null);
        }}
        selectedItem={{
          id: selectedCountryCode,
          type: "text",
          text: getCountryNameFromCode(selectedCountryCode),
          value: selectedCountryCode,
        }}
        size="xs"
      />

      {actions ? (
        <AutoCompleteInput<AddressAutocompleteFields, string>
          dataTestId="address-street1-input"
          getSuggestions={getAddressSuggestionsFromAddressLine}
          label={t("Address")}
          pickSuggestion={getAddressFromAddressLineSuggestion}
          placeholder={t("Enter address")}
          setAutocompleteFields={setAddressFields}
          setValue={(value) => {
            form.inputs.address1.setValue(value);
          }}
          value={form.value.address1}
        />
      ) : (
        <RedoTextInput
          dataTestId="address-street1-input"
          label={t("Address")}
          placeholder={t("Enter address")}
          setValue={form.inputs.address1.setValue}
          value={form.value.address1}
        />
      )}

      <RedoTextInput
        dataTestId="address-street2-input"
        label={t("Apartment, suite, etc. (optional)")}
        placeholder={t("Enter your number")}
        setValue={form.inputs.address2.setValue}
        value={form.value.address2 ?? ""}
      />

      <Flex dir="row">
        <RedoTextInput
          dataTestId="city-input"
          label={t("City")}
          placeholder={t("Enter your city")}
          setValue={form.inputs.city.setValue}
          value={form.value.city}
        />
        <Flex dir="column" gap="sm" grow={1}>
          {useManualProvinceEntry || availableProvinces.length === 0 ? (
            <RedoTextInput
              dataTestId="state-input"
              label={
                selectedCountryCode === RedoShopifyCountryCode.Us
                  ? t("State")
                  : t("Province")
              }
              placeholder={t("Enter state/province")}
              setValue={form.inputs.province.setValue}
              value={form.value.province ?? ""}
            />
          ) : (
            <RedoSingleSelectDropdownInput<string | null>
              dataTestId="state-input"
              label={
                selectedCountryCode === RedoShopifyCountryCode.Us
                  ? t("State")
                  : t("Province")
              }
              options={[
                {
                  id: "none",
                  type: "text" as const,
                  text: t("None"),
                  value: null,
                },
                ...Object.values(availableProvinces).map(({ province }) => ({
                  id: province,
                  type: "text" as const,
                  text: province,
                  value: province,
                })),
              ]}
              optionSelected={({ item: province }) => {
                form.inputs.province.setValue(province.value);
              }}
              selectedItem={
                form.value.province
                  ? {
                      id: form.value.province,
                      type: "text" as const,
                      text: form.value.province,
                      value: form.value.province,
                    }
                  : {
                      id: "none",
                      type: "text" as const,
                      text: t("None"),
                      value: null,
                    }
              }
              size="xs"
            />
          )}
          {availableProvinces.length > 0 && (
            <RedoCheckbox
              label={t("Enter manually")}
              setValue={(value) => setUseManualProvinceEntry(value === true)}
              size="xs"
              value={useManualProvinceEntry}
            />
          )}
        </Flex>
      </Flex>

      <RedoTextInput
        dataTestId="zip-input"
        label={t("Zip")}
        placeholder={t("Enter your zip")}
        setValue={form.inputs.zip.setValue}
        value={form.value.zip}
      />

      <RedoTextInput
        dataTestId="phone-input"
        errorText={
          requirePhone && form.changed && form.inputs.phone.allErrors.length > 0
            ? form.inputs.phone.allErrors.join(", ")
            : undefined
        }
        label={requirePhone ? t("Phone number (required)") : t("Phone number")}
        placeholder={
          requirePhone
            ? t("Enter phone number (required)")
            : t("Enter phone number (optional)")
        }
        setValue={form.inputs.phone.setValue}
        value={form.value.phone ?? ""}
      />

      {!hideEmailField && (
        <RedoTextInput
          dataTestId="email-input"
          errorText={form.inputs.email.allErrors?.[0]}
          label={requireEmail ? t("Email") : t("Email (optional)")}
          placeholder={requireEmail ? "*" : ""}
          setValue={form.inputs.email.setValue}
          value={form.value.email ?? ""}
        />
      )}
    </Flex>
  );
});
